import {
  normalizeEvidenceFacts,
  normalizeSourceRecords,
} from "./responseData.mjs";
import { normalizeResponseProjections } from "./responseProjection.mjs";

const PAGE_LEAVES = {
  "page.facility.dashboard": "dashboard",
  "page.facility.workorder": "workorder",
  "page.facility.electricity": "electricity",
  "page.facility.gas": "gas",
  "page.facility.water": "water",
  "page.facility.equipment": "equipment",
  "page.facility.utility.summary": "utility-summary",
  "page.facility.savings": "savings",
};

const safeText = (value, limit = 300) =>
  typeof value === "string" ? value.replace(/\0/g, " ").trim().slice(0, limit) : "";

const safeWebUrl = (value) => {
  const url = safeText(value, 2048);
  return /^https?:\/\//i.test(url) ? url : "";
};

export const normalizeResponseManifest = (raw) => {
  if (!raw || raw.version !== 1 || !Array.isArray(raw.citations)) return null;
  const seen = new Set();
  const citations = raw.citations.slice(0, 16).reduce((result, item) => {
    const id = safeText(item?.id, 32);
    const sourceType = safeText(item?.source_type, 32);
    if (!/^cit-[1-9][0-9]*$/.test(id) || seen.has(id)) return result;
    if (!["facility_api", "product_knowledge", "web", "business_time", "page_context"].includes(sourceType)) {
      return result;
    }
    const pageId = PAGE_LEAVES[item?.page_id] ? item.page_id : null;
    const url = sourceType === "web" ? safeWebUrl(item?.url) : "";
    if (sourceType === "web" && !url) return result;
    seen.add(id);
    result.push({
      id,
      sourceType,
      sourceKey: safeText(item?.source_key, 120),
      label: safeText(item?.label, 200) || "Verified source",
      pageId,
      url,
      periods: Array.isArray(item?.periods) ? item.periods.slice(0, 8) : [],
      records: normalizeSourceRecords(item?.records),
    });
    return result;
  }, []);
  if (!citations.length) return null;
  const citationIds = new Set(citations.map((item) => item.id));
  const facts = normalizeEvidenceFacts(raw.facts, citationIds);
  const calculations = Array.isArray(raw.calculations)
    ? raw.calculations.slice(0, 12).filter((item) =>
        Array.isArray(item?.citation_ids) && item.citation_ids.some((id) => citationIds.has(id))
      )
    : [];
  const requirementStatuses = new Set([
    "supported",
    "partial",
    "unsupported",
    "ambiguous",
    "pending",
  ]);
  const requirements = Array.isArray(raw.requirements)
    ? raw.requirements.slice(0, 12).reduce((result, item) => {
        const sequence = Number(item?.sequence);
        const status = safeText(item?.status, 32);
        if (!Number.isInteger(sequence) || sequence < 1 || !requirementStatuses.has(status)) {
          return result;
        }
        result.push({
          sequence,
          part: safeText(item?.part, 240) || `Answer part ${sequence}`,
          intent: safeText(item?.intent, 40),
          status,
          citationIds: Array.isArray(item?.citation_ids)
            ? item.citation_ids.filter((id) => citationIds.has(id)).slice(0, 16)
            : [],
          factIds: Array.isArray(item?.fact_ids)
            ? item.fact_ids.filter((id) => /^fact-[1-9][0-9]*$/.test(id)).slice(0, 50)
            : [],
          limitation: safeText(item?.limitation, 800),
        });
        return result;
      }, [])
    : [];
  const projections = normalizeResponseProjections(raw.projections, citationIds);
  return { version: 1, citations, facts, calculations, requirements, projections };
};

export const citationLeaf = (citation) => PAGE_LEAVES[citation?.pageId] || null;

export const citationIdFromHref = (href) => {
  const match = /^#citation-(cit-[1-9][0-9]*)$/.exec(href || "");
  return match ? match[1] : null;
};
